import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';

import {COURSES_PAGE_SIZE, INSTRUCTOR_PROFILE_FIELDS} from './constants';
import type {
    CourseOption,
    CropImagePayload,
    CroppedImageResponse,
    FieldErrors,
    InstructorProfile,
    InstructorProfilePatchPayload,
    SupportedLocale,
    UserMeFields,
    UserPrivacyFields,
    ValidationItem,
} from './types';


/**
 * Fetch the current user's privacy settings.
 */
export async function fetchPrivacySettings(): Promise<UserPrivacyFields> {
    const headers = getMonolithApiHeaders();
    const res = await fetch(
        '/api-2.0/users/me/?fields[user]=profile_visible,course_list_visible',
        {headers},
    );
    if (!res.ok) {
        throw new Error('Failed to fetch privacy settings');
    }
    return res.json();
}

/**
 * Fetch the current user's instructor profile.
 */
export async function fetchInstructorProfile(): Promise<InstructorProfile> {
    const headers = getMonolithApiHeaders();
    const res = await fetch(
        `/api-2.0/users/instructor-profile/me/?fields[user]=${INSTRUCTOR_PROFILE_FIELDS.join(',')}`,
        {headers},
    );
    if (!res.ok) {
        throw new Error('Failed to fetch instructor profile');
    }
    return res.json();
}

/**
 * Fetch the current user's profile photo URL.
 */
export async function fetchUserPhoto(): Promise<UserMeFields> {
    const headers = getMonolithApiHeaders();
    const res = await fetch('/api-2.0/users/me/?fields[user]=image_200_H', {headers});
    if (!res.ok) {
        throw new Error('Failed to fetch user photo');
    }
    return res.json();
}

/**
 * Patch the current user's settings (privacy, photo, etc.).
 * When sending an image file, pass a FormData instance as body.
 */
export async function patchUserSettings(
    body: Record<string, unknown> | FormData,
): Promise<Response> {
    const headers = getMonolithApiHeaders();
    const isFormData = body instanceof FormData;
    return fetch('/api-2.0/users/me/', {
        method: 'PATCH',
        headers: isFormData ? headers : {...headers, 'Content-Type': 'application/json'},
        body: isFormData ? body : JSON.stringify(body),
    });
}

/**
 * Normalize the API error body into the shared `FieldErrors` shape.
 * The monolith may return either:
 *   - `{field: ["msg"]}` (flat string arrays from DRF)
 *   - `{field: [{messages: ["msg"], type: "error"}]}` (structured ValidationItem)
 */
function normalizeApiErrors(raw: Record<string, unknown>): FieldErrors {
    const result: FieldErrors = {};
    for (const [field, value] of Object.entries(raw)) {
        if (!Array.isArray(value) || value.length === 0) continue;
        if (typeof value[0] === 'string') {
            result[field] = [{messages: value as string[], type: 'error'}];
        } else {
            result[field] = value as ValidationItem[];
        }
    }
    return result;
}

/**
 * Patch the instructor profile.
 * On success returns the updated profile from the response body — callers do
 * not need a follow-up GET.  On failure returns normalised field errors.
 */
export async function patchInstructorProfile(
    payload: InstructorProfilePatchPayload,
): Promise<{ok: true; data: InstructorProfile} | {ok: false; errors?: FieldErrors}> {
    const headers = getMonolithApiHeaders();
    const res = await fetch('/api-2.0/users/instructor-profile/me/', {
        method: 'PATCH',
        headers: {...headers, 'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    });
    if (res.ok) {
        const data: InstructorProfile = await res.json();
        return {ok: true, data};
    }
    const raw = await res.json().catch(() => ({}));
    const errors = normalizeApiErrors(raw);
    return {ok: false, errors};
}

/**
 * Fetch a page of courses the instructor has published.
 * Used to populate the Featured Courses autosuggest dropdowns.
 */
export async function fetchInstructorCourses(
    instructorId: number,
    page: number,
): Promise<{results: CourseOption[]; count: number}> {
    const headers = getMonolithApiHeaders();
    const params = new URLSearchParams({
        page: String(page),
        page_size: String(COURSES_PAGE_SIZE),
        ordering: 'lang,-course_performance__revenue_30days',
        'fields[course]': 'id,title,url,headline,image_100x100,image_480x270,image_750x422,rating,num_reviews,num_subscribers,visible_instructors',
    });
    const res = await fetch(
        `/api-2.0/users/${instructorId}/taught-profile-courses/?${params}`,
        {headers},
    );
    if (!res.ok) {
        throw new Error('Failed to fetch instructor courses');
    }
    return res.json();
}

export interface CourseReviewUser {
    title: string;
    public_display_name: string;
}

export interface CourseReview {
    id: number;
    rating: number;
    content: string;
    created_formatted_with_time_since: string;
    user: CourseReviewUser;
    /** Annotated client-side with the course title. */
    courseTitle?: string;
}

/**
 * Fetch text reviews for a single course, ordered by review score.
 */
export async function fetchCourseReviews(
    courseId: number,
    pageSize: number,
): Promise<{results: CourseReview[]; count: number}> {
    const headers = getMonolithApiHeaders();
    const params = new URLSearchParams({
        'fields[course_review]': 'id,rating,content,created_formatted_with_time_since,user',
        'fields[user]': 'title,public_display_name',
        is_text_review: '1',
        ordering: 'course_review_score__rank,-created',
        page_size: String(pageSize),
    });
    const res = await fetch(`/api-2.0/courses/${courseId}/reviews/?${params}`, {headers});
    if (!res.ok) {
        throw new Error(`Failed to fetch reviews for course ${courseId}`);
    }
    return res.json();
}

/**
 * Fetch supported locales for the language dropdown.
 */
export async function fetchSupportedLocales(): Promise<SupportedLocale[]> {
    const headers = getMonolithApiHeaders();
    const res = await fetch(
        '/api-2.0/locales/?supported_languages=true&page_size=999&ordering=title',
        {headers},
    );
    if (!res.ok) {
        throw new Error('Failed to fetch supported locales');
    }
    const data = await res.json();
    return data.results ?? data;
}

/**
 * Fetch S3 upload configuration (bucket URL and access key) for image uploads.
 */
// export async function fetchS3UploadConfig(): Promise<S3UploadConfig> {
//     const headers = getMonolithApiHeaders();
//     const res = await fetch('/api-2.0/s3-upload-config/', {headers});
//     if (!res.ok) {
//         throw new Error('Failed to fetch S3 upload config');
//     }
//     return res.json();
// }

/**
 * Structured error thrown by {@link postCroppedImage} when the crop API
 * returns a non-2xx response.  Preserves the HTTP status and response body
 * so callers can show context-aware error messages.
 */
export class CropApiError extends Error {
    status: number;
    responseData: string;
    statusText: string;

    constructor(status: number, responseData: string, statusText: string) {
        super(`Crop API error: ${status} ${statusText}`);
        this.name = 'CropApiError';
        this.status = status;
        this.responseData = responseData;
        this.statusText = statusText;
    }
}

/**
 * Submit crop coordinates and S3 asset data for server-side image cropping.
 *
 * TODO(legacy-bucket): The `/api-2.0/cropped-images/` endpoint validates the bucket name
 * in the payload against an allowlist. When the UploadApplication changes the target
 * bucket, ensure the crop service's allowlist includes the new bucket. If it doesn't,
 * requests will fail with 400 "unexpected netloc".
 */
export async function postCroppedImage(data: CropImagePayload): Promise<CroppedImageResponse> {
    const headers = getMonolithApiHeaders();
    const res = await fetch('/api-2.0/cropped-images/', {
        method: 'POST',
        headers: {...headers, 'Content-Type': 'application/json'},
        body: JSON.stringify(data),
    });
    if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        throw new CropApiError(res.status, errorText, res.statusText);
    }
    return res.json();
}
